import {
  blockFromDrag,
  BlockStatus,
  normalizeBlockTitle,
  PickerDraftKind,
  PickerFlushReason,
  type LiveTimeBlock,
  type Span,
} from '@wiztime/core';
import type { EditBlocksPatch } from '@wiztime/storage';
import { useEffect, useLayoutEffect, useMemo, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutMode, useLayoutMode } from '../../../hooks/useMediaQuery';
import { Popover } from '../../../ui/Popover';
import { Sheet } from '../../../ui/Sheet';
import { toastInfo } from '../../../shell/toastStore';
import { useDayData, type DayData } from '../DayDataContext';
import { topLevelSelectable } from '../projectGroups';
import { useProjectGroups } from '../useProjectGroups';
import { PickerKind, useDayUi, type AnchorRect, type PickerState } from '../dayUiStore';
import { useDayCommands } from '../useDayCommands';
import type { DisplayGroup } from '../timeline/laneModel';
import { ConflictResolver } from './ConflictResolver';
import { clearPickerDraft, loadPickerDraft, savePickerDraft } from './pickerDraftStorage';
import { registerPickerFlushHandler, type PickerFlushOptions } from './pickerFlush';
import { pickerDiff, type PickerBaseline } from './pickerModel';
import { ProjectPicker } from './ProjectPicker';

interface FrameProps {
  readonly narrow: boolean;
  readonly label: string;
  readonly anchor: AnchorRect;
  readonly onClose: () => void;
  readonly onEscape?: () => void;
  readonly children: ReactNode;
}

/** A popover beside the anchor on desktop, a bottom sheet on phones. */
function PickerFrame({ narrow, label, anchor, onClose, onEscape, children }: FrameProps) {
  return narrow ? (
    <Sheet label={label} onClose={onClose} onEscape={onEscape}>
      {children}
    </Sheet>
  ) : (
    <Popover anchor={anchor} label={label} onClose={onClose} onEscape={onEscape}>
      {children}
    </Popover>
  );
}

/** First non-empty note among the group's members, in start order (2026-10b §5.5). */
function groupNote(members: readonly LiveTimeBlock[]): string {
  for (const member of members) {
    if (member.data.note !== null && member.data.note !== '') return member.data.note;
  }
  return '';
}

/** Whether the merged group's members carry differing notes (2026-10b §5.5). */
function notesDiffer(members: readonly LiveTimeBlock[]): boolean {
  if (members.length < 2) return false;
  const first = members[0]?.data.note ?? null;
  return members.some((member) => (member.data.note ?? null) !== first);
}

function baselineOf(picker: PickerState, group: DisplayGroup | undefined, data: DayData): PickerBaseline {
  const dayStartMin = data.settings.dayStartMin;
  const stepMin = data.settings.timeStepMin;
  if (picker.kind === PickerKind.Create || group === undefined) {
    const span = picker.kind === PickerKind.Create ? picker.span : { startMin: 0, endMin: 0 };
    return {
      blockIds: [],
      date: picker.date,
      startMin: span.startMin,
      endMin: span.endMin,
      title: '',
      note: '',
      dayStartMin,
      stepMin,
      spanEditable: true,
      isDraft: false,
    };
  }
  return {
    blockIds: group.memberIds,
    date: picker.date,
    startMin: group.startMin,
    endMin: group.endMin,
    title: group.title ?? '',
    note: groupNote(group.members),
    dayStartMin,
    stepMin,
    // The start and end of a merged group are read-only in the picker (2026-10b §3.8).
    spanEditable: group.members.length === 1,
    isDraft: group.status === BlockStatus.Draft,
  };
}

/** Anchor for a picker restored without an opening element: beside the lane's upper middle. */
function fallbackAnchor(): AnchorRect {
  const lane = document.querySelector('[data-testid="lane"]');
  const rect = lane?.getBoundingClientRect();
  if (rect !== undefined && rect.width > 0) {
    const x = rect.left + rect.width / 2;
    return { left: x - 1, top: rect.top + 80, right: x + 1, bottom: rect.top + 120 };
  }
  const x = window.innerWidth / 2;
  const y = window.innerHeight / 3;
  return { left: x - 1, top: y, right: x + 1, bottom: y + 40 };
}

/**
 * Renders the project picker or conflict resolver as a popover (desktop) or bottom sheet
 * (phone), and owns the picker's single commit point (2026-10b §5.4): it registers the
 * `flushPicker` handler, commits field drafts on every close path, stashes them to
 * sessionStorage on page hide, and restores the stash on load / page show.
 */
export function PickerHost() {
  const { t } = useTranslation();
  const data = useDayData();
  const rankedGroups = useProjectGroups(data.projects.active, data.projects);
  const commands = useDayCommands();
  const picker = useDayUi((state) => state.picker);
  const conflict = useDayUi((state) => state.conflict);
  const closePicker = useDayUi((state) => state.closePicker);
  const closeConflict = useDayUi((state) => state.closeConflict);
  const openPicker = useDayUi((state) => state.openPicker);
  const openConflict = useDayUi((state) => state.openConflict);
  const narrow = useLayoutMode() === LayoutMode.Narrow;

  const group =
    picker?.kind === PickerKind.Edit
      ? data.groups.find((candidate) => candidate.key === picker.groupKey)
      : undefined;
  const baseline = useMemo(
    () => (picker === null ? null : baselineOf(picker, group, data)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [picker, group, data.settings.dayStartMin, data.settings.timeStepMin],
  );

  // Everything the flush handler and the listeners need, without re-registering per render.
  const latest = useRef({ data, commands, t });
  useEffect(() => {
    latest.current = { data, commands, t };
  });

  /** Commit `patch` to `ids` as one `editBlocks` command; rebaseline first (idempotence). */
  const commitFields = (ids: readonly string[], patch: EditBlocksPatch, toast: boolean): Promise<boolean> => {
    const { commands: api, t: translate } = latest.current;
    const ui = useDayUi.getState();
    const fields = ui.pickerFields;
    if (fields !== null) {
      ui.rebaselinePicker({
        ...fields.baseline,
        title: patch.title === undefined ? fields.baseline.title : (patch.title ?? ''),
        note: patch.note === undefined ? fields.baseline.note : (patch.note ?? ''),
        startMin: patch.span?.startMin ?? fields.baseline.startMin,
        endMin: patch.span?.endMin ?? fields.baseline.endMin,
      });
    }
    const parts = [
      ...(patch.title !== undefined ? [translate('picker.fieldTitle')] : []),
      ...(patch.note !== undefined ? [translate('picker.fieldNote')] : []),
      ...(patch.span !== undefined ? [translate('picker.fieldTime')] : []),
    ];
    const message = toast
      ? translate('toast.pickerSaved', { fields: parts.join(translate('picker.fieldsSeparator')) })
      : undefined;
    return api.editBlocks(ids, patch, message);
  };

  /** The single commit point (2026-10b §5.4). Returns false to veto the close. */
  const handleFlush = (reason: PickerFlushReason, options: PickerFlushOptions): boolean => {
    const ui = useDayUi.getState();
    const open = ui.picker;
    const fields = ui.pickerFields;
    if (open === null || fields === null) return true;
    const diff = pickerDiff(fields);
    const { data: day, t: translate } = latest.current;

    if (open.kind === PickerKind.Create) {
      if (reason === PickerFlushReason.Hide) {
        // Stash the create context; the picker itself stays open (§5.4 path 8).
        const span: Span =
          diff.span !== null && diff.timeError === null
            ? diff.span
            : { startMin: fields.baseline.startMin, endMin: fields.baseline.endMin };
        savePickerDraft({
          kind: PickerDraftKind.Create,
          date: open.date,
          span,
          title: fields.title,
          note: fields.note,
          savedAt: Date.now(),
        });
        return true;
      }
      if (reason !== PickerFlushReason.Dismiss) return true;
      if (!diff.dirty) return true;
      if (!fields.noticeShown) {
        // First dismissal of a dirty create context: warn, shake, keep the picker (§5.4).
        ui.showPickerNotice();
        return false;
      }
      // Second dismissal abandons; a deliberate abandonment is never stashed (§5.4).
      clearPickerDraft();
      return true;
    }

    // Existing-block context.
    if (reason === PickerFlushReason.Delete) {
      // Deleting the block commits nothing: the unsaved edits go with the block (§5.4).
      clearPickerDraft();
      return true;
    }
    if (!diff.dirty) {
      if (reason !== PickerFlushReason.Hide) clearPickerDraft();
      return true;
    }
    const patch = diff.patch;
    const hasPatch = patch.title !== undefined || patch.note !== undefined || patch.span !== undefined;
    const timeRejected = diff.timeDirty && diff.timeError !== null;

    if (reason === PickerFlushReason.Hide) {
      if (!hasPatch) return true;
      // Backstop for the async IndexedDB write (§5.4): stash value + baseline per field, and
      // remove the stash once the write is known to have landed.
      savePickerDraft({
        kind: PickerDraftKind.Existing,
        date: open.date,
        blockIds: fields.baseline.blockIds,
        fields: {
          ...(patch.title !== undefined
            ? { title: { value: patch.title, baseline: normalizeBlockTitle(fields.baseline.title) } }
            : {}),
          ...(patch.note !== undefined
            ? {
                note: {
                  value: patch.note,
                  baseline: fields.baseline.note === '' ? null : fields.baseline.note,
                },
              }
            : {}),
          ...(patch.span !== undefined
            ? {
                span: {
                  value: patch.span,
                  baseline: { startMin: fields.baseline.startMin, endMin: fields.baseline.endMin },
                },
              }
            : {}),
        },
        savedAt: Date.now(),
      });
      void commitFields(fields.baseline.blockIds, patch, false).then((ok) => {
        if (ok) clearPickerDraft();
      });
      return true;
    }

    // Dismiss or Expire.
    let ids = fields.baseline.blockIds;
    if (reason === PickerFlushReason.Expire) {
      const live = new Set(day.blocks.map((block) => block.id));
      ids = ids.filter((id) => live.has(id));
      if (ids.length === 0) {
        toastInfo(translate('toast.pickerExpired'));
        clearPickerDraft();
        return true;
      }
    }
    if (timeRejected) toastInfo(translate('toast.timeNotSaved'));
    if (hasPatch) {
      void commitFields(ids, patch, reason === PickerFlushReason.Dismiss && options.silent !== true);
    }
    clearPickerDraft();
    return true;
  };

  const handleFlushRef = useRef(handleFlush);
  useEffect(() => {
    handleFlushRef.current = handleFlush;
  });

  // Register the single commit point; on unmount (leaving the day/week views) commit silently
  // and close — the picker cannot stay open without its host.
  useEffect(() => {
    const unregister = registerPickerFlushHandler((reason, options) =>
      handleFlushRef.current(reason, options),
    );
    return () => {
      const ui = useDayUi.getState();
      if (ui.picker !== null) {
        handleFlushRef.current(PickerFlushReason.Dismiss, { silent: true });
        ui.closePickerDiscarding();
      }
      unregister();
    };
  }, []);

  // Install the field baselines whenever the picker's target changes (before paint, so the
  // first keystroke already edits store-held drafts).
  const targetKey =
    picker === null
      ? null
      : picker.kind === PickerKind.Create
        ? `create:${picker.date}:${picker.span.startMin}-${picker.span.endMin}`
        : `edit:${picker.groupKey}`;
  useLayoutEffect(() => {
    if (picker === null || baseline === null) return;
    const ui = useDayUi.getState();
    ui.setPickerBaseline(baseline);
    if (picker.kind === PickerKind.Create && picker.restored !== undefined) {
      // Restored stash: the texts are pre-filled but stay "modified" against empty baselines.
      ui.setPickerText('title', picker.restored.title);
      ui.setPickerText('note', picker.restored.note);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey]);

  // Page hide commits (path 8); page show and load restore the stash (§5.4).
  useEffect(() => {
    const tryRestore = (): void => {
      const { data: day, commands: api } = latest.current;
      if (day.blocksLoading) return;
      const ui = useDayUi.getState();
      const draft = loadPickerDraft(Date.now());
      if (draft === null) return;
      // The picker survived in memory: the stash is redundant, whatever its kind (§5.4).
      if (ui.picker !== null) {
        clearPickerDraft();
        return;
      }
      if (draft.date !== day.date) return;
      clearPickerDraft();
      if (draft.kind === PickerDraftKind.Create) {
        // Re-clamp against today's blocks (§3.3): the stashed span may have been taken by
        // blocks that arrived through sync while the tab was hidden.
        const options = {
          step: day.settings.timeStepMin,
          minDuration: day.settings.minDurationMin,
          dayStartMin: day.settings.dayStartMin,
        };
        const others = day.blocks.map((block) => ({
          startMin: block.data.startMin,
          endMin: block.data.endMin,
        }));
        const span = blockFromDrag(draft.span.startMin, draft.span.endMin, options, others) ?? draft.span;
        ui.openPicker({
          kind: PickerKind.Create,
          date: draft.date,
          span,
          anchor: fallbackAnchor(),
          restored: { title: draft.title, note: draft.note },
        });
        return;
      }
      // Existing-block backstop: write a stashed field only where the stored value still
      // equals the baseline it was committed over — never overwrite another device's edit.
      const byId = new Map(day.blocks.map((block) => [block.id, block]));
      const live = draft.blockIds.filter((id) => byId.has(id));
      const first = live[0] === undefined ? undefined : byId.get(live[0]);
      if (first === undefined) return;
      const patch: { title?: string | null; note?: string | null; span?: Span } = {};
      if (
        draft.fields.title !== undefined &&
        normalizeBlockTitle(first.data.title) === normalizeBlockTitle(draft.fields.title.baseline)
      ) {
        patch.title = draft.fields.title.value;
      }
      if (draft.fields.note !== undefined && (first.data.note ?? null) === draft.fields.note.baseline) {
        patch.note = draft.fields.note.value;
      }
      if (
        draft.fields.span !== undefined &&
        live.length === 1 &&
        first.data.startMin === draft.fields.span.baseline.startMin &&
        first.data.endMin === draft.fields.span.baseline.endMin
      ) {
        patch.span = draft.fields.span.value;
      }
      if (patch.title !== undefined || patch.note !== undefined || patch.span !== undefined) {
        void api.editBlocks(live, patch);
      }
    };

    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') {
        handleFlushRef.current(PickerFlushReason.Hide, {});
      } else {
        tryRestore();
      }
    };
    const onPageHide = (): void => {
      handleFlushRef.current(PickerFlushReason.Hide, {});
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    tryRestore();
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
    // Also retried once the day's blocks finished loading (the restore needs them).
  }, [data.blocksLoading, data.date]);

  // An edit picker whose target is gone: date switched away (Dismiss, already committed by the
  // route path) or the group vanished under sync (Expire, commit to the surviving records).
  const dismissStale = picker !== null && picker.kind === PickerKind.Edit && picker.date !== data.date;
  const expireStale =
    picker !== null &&
    picker.kind === PickerKind.Edit &&
    picker.date === data.date &&
    group === undefined &&
    !data.blocksLoading;
  useEffect(() => {
    if (!dismissStale && !expireStale) return;
    const ui = useDayUi.getState();
    handleFlushRef.current(dismissStale ? PickerFlushReason.Dismiss : PickerFlushReason.Expire, {
      silent: true,
    });
    ui.closePickerDiscarding();
  }, [dismissStale, expireStale]);

  // The overlap popover closes with its overlap: block deleted, trimmed apart, or date changed.
  const conflictStale =
    conflict !== null &&
    (conflict.date !== data.date ||
      (!data.blocksLoading &&
        !data.conflicts.pairs.some(([a, b]) => a === conflict.id || b === conflict.id)));
  useEffect(() => {
    if (conflictStale) closeConflict();
  }, [conflictStale, closeConflict]);

  if (conflict !== null && conflict.date === data.date) {
    const subject = data.blocks.find((candidate) => candidate.id === conflict.id);
    const subjectName =
      subject === undefined
        ? t('timeline.unknownProject')
        : (data.projects.byId.get(subject.data.projectId)?.data.name ?? t('timeline.unknownProject'));
    return (
      <PickerFrame
        narrow={narrow}
        label={t('overlap.title', { project: subjectName })}
        onClose={closeConflict}
        anchor={conflict.anchor}
      >
        <ConflictResolver
          id={conflict.id}
          onRecount={() => {
            closeConflict();
            void commands.recountOverlap(conflict.id);
          }}
          onResolve={(otherId, mode) => {
            closeConflict();
            void commands.resolveConflict(conflict.id, otherId, mode);
          }}
        />
      </PickerFrame>
    );
  }
  if (picker === null || baseline === null || dismissStale || expireStale) return null;

  // The digit keys pick by settings order, exactly as the quick-mark tiles do (§4.8, §5.3).
  const digitProjects = topLevelSelectable(data.projects.all, data.projects.byId);

  /**
   * The picker's pending content, taken at the moment a project is chosen: the
   * time rejection notice, then — for a create context — the span, title and note of the new
   * block, or — for an existing group — the patch of the modified fields (§5.4).
   */
  const takeDraft = ():
    | { readonly kind: 'create'; readonly span: Span; readonly title: string | null; readonly note: string | null }
    | { readonly kind: 'edit'; readonly patch: Omit<EditBlocksPatch, 'projectId'> } => {
    const ui = useDayUi.getState();
    const fields = ui.pickerFields;
    const diff = fields === null ? null : pickerDiff(fields);
    const timeRejected = diff !== null && diff.timeDirty && diff.timeError !== null;
    if (timeRejected) toastInfo(t('toast.timeNotSaved'));
    if (picker.kind === PickerKind.Create) {
      const span =
        diff !== null && diff.timeDirty && diff.timeError === null && diff.span !== null
          ? diff.span
          : picker.span;
      const title = fields?.title ?? '';
      const note = fields?.note.trim() ?? '';
      return { kind: 'create', span, title: title === '' ? null : title, note: note === '' ? null : note };
    }
    return { kind: 'edit', patch: diff?.patch ?? {} };
  };

  /** Commit everything (project plus modified fields) and close: Commit(project) of §5.4. */
  const chooseProject = (projectId: string): void => {
    const ui = useDayUi.getState();
    const draft = takeDraft();
    ui.closePickerDiscarding();
    clearPickerDraft();
    if (draft.kind === 'create') {
      void commands.createBlock(picker.date, draft.span, projectId, { note: draft.note, title: draft.title });
      return;
    }
    if (group === undefined) return;
    void commands.editBlocks(group.memberIds, { ...draft.patch, projectId });
  };

  /**
   * 「取消」 and Esc: close without committing the field drafts (nothing is written, so there is
   * nothing to undo); a create context is abandoned. A press outside still saves (§5.4).
   */
  const cancel = (): void => {
    clearPickerDraft();
    useDayUi.getState().closePickerDiscarding();
  };

  if (picker.kind === PickerKind.Create) {
    return (
      <PickerFrame
        narrow={narrow}
        label={t('picker.title')}
        onClose={closePicker}
        onEscape={cancel}
        anchor={picker.anchor}
      >
        <ProjectPicker
          baseline={baseline}
          creating
          mergedCount={1}
          notesDiffer={false}
          projects={data.projects.active}
          groups={rankedGroups}
          projectsById={data.projects.byId}
          digitProjects={digitProjects}
          currentProjectId={null}
          focus={picker.focus}
          narrow={narrow}
          overlapCount={0}
          onChoose={chooseProject}
          onSaveClose={closePicker}
          onCancel={cancel}
        />
      </PickerFrame>
    );
  }

  if (group === undefined) return null;
  const overlapCount = data.conflicts.pairs.filter(([a, b]) => a === group.key || b === group.key).length;
  return (
    <PickerFrame
      narrow={narrow}
      label={t('picker.title')}
      onClose={closePicker}
      onEscape={cancel}
      anchor={picker.anchor}
    >
      <ProjectPicker
        baseline={baseline}
        creating={false}
        mergedCount={group.members.length}
        notesDiffer={notesDiffer(group.members)}
        projects={data.projects.active}
        groups={rankedGroups}
        projectsById={data.projects.byId}
        digitProjects={digitProjects}
        currentProjectId={group.projectId}
        focus={picker.focus}
        narrow={narrow}
        overlapCount={overlapCount}
        onChoose={chooseProject}
        currentProject={data.projects.byId.get(group.projectId) ?? null}
        // "Record an overlapping entry here" (ui-redesign §2.1.2): reopen as a create picker
        // over the group's whole range (openPicker commits the field drafts first, §5.4).
        onOverlay={() =>
          openPicker({
            kind: PickerKind.Create,
            date: picker.date,
            span: { startMin: group.startMin, endMin: group.endMin },
            anchor: picker.anchor,
          })
        }
        onViewConflict={
          overlapCount > 0 && group.status === BlockStatus.Confirmed
            ? () => openConflict({ date: picker.date, id: group.key, anchor: picker.anchor })
            : undefined
        }
        onConfirm={() => {
          // Saving any field already confirms the draft; a clean picker confirms explicitly.
          const ui = useDayUi.getState();
          const fields = ui.pickerFields;
          const dirty = fields !== null && pickerDiff(fields).dirty;
          handleFlushRef.current(PickerFlushReason.Dismiss, { silent: true });
          ui.closePickerDiscarding();
          if (!dirty) void commands.confirmGroup(group);
        }}
        onDelete={() => {
          // Del and the delete row never commit fields first (§5.4): the unsaved edits go
          // with the block; undo restores the block as last saved.
          const ui = useDayUi.getState();
          handleFlushRef.current(PickerFlushReason.Delete, {});
          ui.closePickerDiscarding();
          void commands.clearGroup(group);
        }}
        onSaveClose={closePicker}
        onCancel={cancel}
      />
    </PickerFrame>
  );
}
